import { ResponseError } from '@ory/client-fetch';

import { logger } from '@/utils';

/**
 * Result interface for the handleOryCall function
 */
interface OryCallResult<T> {
  data?: T;
  statusCode: number;
  error?: Error;
}

/**
 * Wrapper for Ory API calls that handles all status codes consistently
 *
 * This utility ensures that 400-499 responses with data are treated as
 * successful responses with data, not as errors. This handles Ory's behavior
 * where authentication flows often return 400 responses with useful data.
 *
 * @param apiCall - Function that makes the Ory API call
 * @param context - Optional context string for logging
 * @returns Object with data, status code, and optional error
 */
const handleOryCall = async <T>(
  apiCall: () => Promise<T>,
  context = 'Ory API',
): Promise<OryCallResult<T>> => {
  let data: T;
  try {
    // Try the normal API call first
    data = await apiCall();

    logger.debug(JSON.stringify(data, undefined, 2));

    return {
      data,
      statusCode: 200,
    };
  } catch (err) {
    if (err instanceof ResponseError) {
      const status = err.response.status;
      const data = err.response;

      // For 400-499 responses with data, treat as valid responses
      if (status >= 400 && status < 500 && status != 429 && data) {
        logger.debug(
          `${context}: Got ${status} with data - treating as valid response`,
          {
            data,
            statusCode: status,
          },
        );

        return {
          statusCode: status,
          error: err,
        };
      }

      // For other errors, return the error and status code
      logger.error(`${context} error:`, {
        statusCode: status,
        message: err.message,
      });

      return {
        statusCode: status,
        error: err,
      };
    }

    return {
      statusCode: 999,
      error: err as Error,
    };
  }
};

export { handleOryCall };
export type { OryCallResult };
